import type { CrmMessages } from "@/components/atomic-crm/providers/commons/englishCrmMessages";

// Italian translation of englishCrmMessages. Glossary: tasks are "Attività",
// the activity log is "Ultime novità" (see docs/tenax/CLAUDE-tenax.md).
export const italianCrmMessages = {
  resources: {
    companies: {
      name: "Organizzazione |||| Organizzazioni",
      forcedCaseName: "Organizzazione",
      fields: {
        name: "Nome dell'organizzazione",
        website: "Sito web",
        linkedin_url: "URL LinkedIn",
        phone_number: "Telefono",
        created_at: "Creata il",
        nb_contacts: "Numero di contatti",
        revenue: "Fatturato",
        sector: "Settore",
        size: "Dimensione",
        tax_identifier: "Partita IVA",
        address: "Indirizzo",
        city: "Città",
        zipcode: "CAP",
        state_abbr: "Provincia",
        country: "Paese",
        description: "Descrizione",
        context_links: "Link utili",
        sales_id: "Responsabile",
      },
      empty: {
        description: "Non ci sono ancora organizzazioni.",
        title: "Nessuna organizzazione trovata",
      },
      import: {
        title: "Importa organizzazioni",
      },
      field_categories: {
        contact: "Recapiti",
        additional_info: "Informazioni aggiuntive",
        address: "Indirizzo",
        context: "Contesto",
      },
      action: {
        create: "Crea organizzazione",
        edit: "Modifica organizzazione",
        new: "Nuova organizzazione",
        show: "Vedi organizzazione",
      },
      added_on: "Aggiunta il %{date}",
      followed_by: "Seguita da %{name}",
      followed_by_you: "Seguita da te",
      no_contacts: "Nessun contatto",
      nb_contacts: "%{smart_count} contatto |||| %{smart_count} contatti",
      nb_deals: "%{smart_count} trattativa |||| %{smart_count} trattative",
      sizes: {
        one_employee: "1 dipendente",
        two_to_nine_employees: "2-9 dipendenti",
        ten_to_forty_nine_employees: "10-49 dipendenti",
        fifty_to_two_hundred_forty_nine_employees: "50-249 dipendenti",
        two_hundred_fifty_or_more_employees: "250 dipendenti o più",
      },
      autocomplete: {
        create_error: "Errore durante la creazione dell'organizzazione",
        create_item: "Crea %{item}",
        create_label: "Scrivi per creare una nuova organizzazione",
      },
    },
    contacts: {
      name: "Contatto |||| Contatti",
      forcedCaseName: "Contatto",
      field_categories: {
        background_info: "Informazioni",
        identity: "Identità",
        misc: "Altro",
        personal_info: "Recapiti",
        position: "Ruolo",
      },
      fields: {
        first_name: "Nome",
        last_name: "Cognome",
        last_seen: "Ultima interazione",
        title: "Ruolo",
        company_id: "Organizzazione",
        email_jsonb: "Indirizzi email",
        email: "Email",
        phone_jsonb: "Numeri di telefono",
        phone_number: "Telefono",
        linkedin_url: "URL LinkedIn",
        background: "Informazioni (chi è, come vi siete conosciuti, ecc.)",
        has_newsletter: "Iscritto alla newsletter",
        sales_id: "Responsabile",
      },
      action: {
        add: "Aggiungi contatto",
        add_first: "Aggiungi il primo contatto",
        create: "Crea contatto",
        edit: "Modifica contatto",
        export_vcard: "Esporta in vCard",
        new: "Nuovo contatto",
        show: "Vedi contatto",
      },
      autocomplete: {
        create_item: "Crea %{item}",
        create_label: "Scrivi per creare un nuovo contatto",
      },
      background: {
        last_activity_on: "Ultima interazione il %{date}",
        added_on: "Aggiunto il %{date}",
        followed_by: "Seguito da %{name}",
        followed_by_you: "Seguito da te",
        status_none: "Nessuno",
      },
      position_at: "%{title} presso",
      position_at_company: "%{title} presso %{company}",
      empty: {
        description: "Non ci sono ancora contatti.",
        title: "Nessun contatto trovato",
      },
      import: {
        title: "Importa contatti",
      },
      inputs: {
        genders: {
          male: "Lui",
          female: "Lei",
          nonbinary: "Non binario",
        },
        personal_info_types: {
          work: "Lavoro",
          home: "Casa",
          other: "Altro",
        },
      },
      list: {
        error_loading: "Errore nel caricamento dei contatti",
      },
      bulk_tag: {
        action: "Tag",
        back: "Torna ai tag",
        create_description:
          "Crea un nuovo tag e applicalo ai contatti selezionati.",
        description:
          "Scegli un tag esistente o creane uno nuovo per i contatti selezionati.",
        empty: "Nessun tag. Creane uno per i contatti selezionati.",
        error: "Impossibile aggiungere il tag ai contatti",
        removed:
          "Tag rimosso da %{smart_count} contatto |||| Tag rimosso da %{smart_count} contatti",
        success:
          "Tag aggiunto a %{smart_count} contatto |||| Tag aggiunto a %{smart_count} contatti",
        title: "Aggiungi un tag ai contatti",
      },
      merge: {
        action: "Unisci a un altro contatto",
        confirm: "Unisci contatti",
        current_contact: "Contatto attuale (verrà eliminato)",
        description: "Unisci questo contatto a un altro.",
        error: "Impossibile unire i contatti",
        merging: "Unione in corso...",
        no_additional_data: "Nessun dato aggiuntivo da unire",
        select_target: "Seleziona il contatto a cui unirlo",
        success: "Contatti uniti",
        target_contact: "Contatto di destinazione (verrà mantenuto)",
        title: "Unisci contatto",
        warning_description:
          "Tutti i dati passeranno al secondo contatto. L'operazione non si può annullare.",
        warning_title: "Attenzione: operazione irreversibile",
        what_will_be_merged: "Cosa verrà unito:",
      },
      filters: {
        before_last_month: "Prima del mese scorso",
        before_this_month: "Prima di questo mese",
        before_this_week: "Prima di questa settimana",
        managed_by_me: "Seguiti da me",
        search: "Cerca nome, organizzazione...",
        this_week: "Questa settimana",
        today: "Oggi",
        tags: "Tag",
        tasks: "Attività",
      },
      hot: {
        empty_change_status:
          'Cambia lo stato di un contatto aggiungendo una nota e cliccando su "mostra opzioni".',
        empty_hint: 'Qui compaiono i contatti con stato "caldo".',
        title: "Contatti caldi",
      },
    },
    deals: {
      name: "Trattativa |||| Trattative",
      fields: {
        name: "Nome",
        description: "Descrizione",
        company_id: "Organizzazione",
        contact_ids: "Contatti",
        categories: "Categorie",
        amount: "Importo",
        expected_closing_date: "Chiusura prevista",
        stage: "Fase",
      },
      action: {
        back_to_deal: "Torna alla trattativa",
        create: "Crea trattativa",
        new: "Nuova trattativa",
      },
      field_categories: {
        misc: "Altro",
      },
      filters: {
        only_mine: "Solo le mie trattative",
      },
      archived: {
        action: "Archivia",
        error: "Errore: trattativa non archiviata",
        list_title: "Trattative archiviate",
        success: "Trattativa archiviata",
        title: "Trattativa archiviata",
        view: "Vedi trattative archiviate",
      },
      inputs: {
        linked_to: "Collegata a",
      },
      unarchived: {
        action: "Rimetti sulla bacheca",
        error: "Errore: trattativa non ripristinata",
        success: "Trattativa ripristinata",
      },
      updated: "Trattativa aggiornata",
      empty: {
        before_create: "prima di creare una trattativa.",
        description: "Non ci sono ancora trattative.",
        title: "Nessuna trattativa trovata",
      },
      import: {
        title: "Importa trattative",
      },
      invalid_date: "Data non valida",
    },
    notes: {
      name: "Nota |||| Note",
      forcedCaseName: "Nota",
      fields: {
        status: "Stato",
        date: "Data",
        attachments: "Allegati",
        contact_id: "Contatto",
        deal_id: "Trattativa",
      },
      action: {
        add: "Aggiungi nota",
        add_first: "Aggiungi la prima nota",
        delete: "Elimina nota",
        edit: "Modifica nota",
        update: "Aggiorna nota",
        add_this: "Aggiungi questa nota",
      },
      sheet: {
        create: "Crea nota",
        create_for: "Crea nota per %{name}",
        edit: "Modifica nota",
        edit_for: "Modifica nota per %{name}",
      },
      deleted: "Nota eliminata",
      empty: "Ancora nessuna nota",
      author_added: "%{name} ha aggiunto una nota",
      you_added: "Hai aggiunto una nota",
      me: "Io",
      list: {
        error_loading: "Errore nel caricamento delle note",
      },
      note_for_contact: "Nota per %{name}",
      stepper: {
        hint: "Apri la scheda di un contatto e aggiungi una nota",
      },
      added: "Nota aggiunta",
      inputs: {
        add_note: "Aggiungi una nota",
        options_hint: "(allega file o cambia i dettagli)",
        show_options: "Mostra opzioni",
      },
      actions: {
        attach_document: "Allega documento",
      },
      validation: {
        note_or_attachment_required: "Serve una nota o un allegato",
      },
    },
    sales: {
      name: "Utente |||| Utenti",
      fields: {
        first_name: "Nome",
        last_name: "Cognome",
        email: "Email",
        secondary_email: "Email secondaria",
        secondary_emails: "Email secondarie",
        administrator: "Amministratore",
        disabled: "Disattivato",
      },
      create: {
        error: "Errore durante la creazione dell'utente.",
        success:
          "Utente creato. Riceverà a breve un'email per impostare la password.",
        title: "Crea un nuovo utente",
      },
      edit: {
        error: "Si è verificato un errore. Riprova.",
        record_not_found: "Elemento non trovato",
        success: "Utente aggiornato",
        title: "Modifica %{name}",
      },
      action: {
        new: "Nuovo utente",
      },
    },
    tasks: {
      name: "Attività |||| Attività",
      forcedCaseName: "Attività",
      fields: {
        text: "Descrizione",
        due_date: "Scadenza",
        type: "Tipo",
        contact_id: "Contatto",
        due_short: "scade",
      },
      action: {
        add: "Aggiungi attività",
        create: "Crea attività",
        edit: "Modifica attività",
      },
      actions: {
        postpone_next_week: "Rimanda alla prossima settimana",
        postpone_tomorrow: "Rimanda a domani",
        title: "azioni sull'attività",
      },
      added: "Attività aggiunta",
      deleted: "Attività eliminata",
      dialog: {
        create: "Crea attività",
        create_for: "Crea attività per %{name}",
      },
      sheet: {
        edit: "Modifica attività",
        edit_for: "Modifica attività per %{name}",
      },
      empty: "Ancora nessuna attività",
      empty_list_hint: "Qui compaiono le attività aggiunte ai tuoi contatti.",
      filters: {
        later: "Più avanti",
        overdue: "Scadute",
        this_week: "Questa settimana",
        today: "Oggi",
        tomorrow: "Domani",
        with_pending: "Con attività da fare",
      },
      regarding_contact: "(Rif.: %{name})",
      updated: "Attività aggiornata",
    },
    tags: {
      name: "Tag |||| Tag",
      action: {
        add: "Aggiungi tag",
        create: "Crea nuovo tag",
      },
      dialog: {
        color: "Colore",
        create_title: "Crea un nuovo tag",
        edit_title: "Modifica tag",
        name_label: "Nome del tag",
        name_placeholder: "Scrivi il nome del tag",
      },
      empty: "Ancora nessun tag.",
    },
  },
  crm: {
    action: {
      reset_password: "Reimposta password",
    },
    auth: {
      first_name: "Nome",
      last_name: "Cognome",
      confirm_password: "Conferma password",
      confirmation_required:
        "Apri il link che ti abbiamo appena inviato per email per confermare l'account.",
      recovery_email_sent:
        "Se sei un utente registrato, riceverai a breve un'email per recuperare la password.",
      sign_in_failed: "Accesso non riuscito.",
      sign_in_google_workspace: "Accedi con Google Workspace",
      signup: {
        create_account: "Crea account",
        create_first_user:
          "Crea il primo account utente per completare la configurazione.",
        creating: "Creazione in corso...",
        initial_user_created: "Primo utente creato",
      },
      welcome_title: "Benvenuto in Tenax CRM",
    },
    common: {
      account_manager: "Responsabile",
      activity: "Ultime novità",
      added: "aggiunto",
      details: "Dettagli",
      last_activity_with_date: "ultima interazione %{date}",
      load_more: "Carica altri",
      misc: "Altro",
      past: "Passata",
      read_more: "Leggi tutto",
      retry: "Riprova",
      show_less: "Mostra meno",
      copied: "Copiato!",
      copy: "Copia",
      loading: "Caricamento...",
      me: "Io",
      task_count: "%{smart_count} attività |||| %{smart_count} attività",
    },
    changelog: {
      title: "Registro delle modifiche",
    },
    activity: {
      added_company: "%{name} ha aggiunto l'organizzazione",
      you_added_company: "Hai aggiunto l'organizzazione",
      added_contact: "%{name} ha aggiunto",
      you_added_contact: "Hai aggiunto",
      added_note: "%{name} ha aggiunto una nota su",
      you_added_note: "Hai aggiunto una nota su",
      added_note_about_deal: "%{name} ha aggiunto una nota sulla trattativa",
      you_added_note_about_deal: "Hai aggiunto una nota sulla trattativa",
      added_deal: "%{name} ha aggiunto la trattativa",
      you_added_deal: "Hai aggiunto la trattativa",
      at_company: "presso",
      to: "a",
      load_more: "Carica altre novità",
    },
    dashboard: {
      deals_chart: "Ricavi attesi dalle trattative",
      deals_pipeline: "Trattative in corso",
      latest_activity: "Ultime novità",
      latest_activity_error: "Errore nel caricamento delle ultime novità",
      latest_notes: "Le mie ultime note",
      latest_notes_added_ago: "aggiunta %{timeAgo}",
      stepper: {
        install: "Installa Tenax CRM",
        progress: "%{step}/3 completati",
        whats_next: "Prossimi passi",
      },
      upcoming_tasks: "Prossime attività",
    },
    data_import: {
      button: "Importa CSV",
      complete:
        "Importazione completata: %{importCount} elementi importati, %{errorCount} errori",
      csv_file: "File CSV",
      error:
        "Impossibile importare questo file: controlla che sia un CSV valido.",
      in_progress: "Importazione in corso…",
      progress:
        "Importati %{importCount} elementi su %{rowCount}, %{errorCount} errori.",
      remaining_time: "Tempo rimanente stimato:",
      resource: "Tipo di dati",
      sample_download: "Scarica un CSV di esempio",
      sample_hint: "Ecco un file CSV di esempio da usare come modello",
      start: "Avvia importazione",
      stop: "Interrompi importazione",
      stopped:
        "Importazione interrotta: %{importCount} elementi importati, %{errorCount} errori",
      title: "Importa dati",
    },
    header: {
      import_data: "Importa da JSON",
    },
    search: {
      title: "Cerca",
      placeholder:
        "Cerca contatti, organizzazioni, trattative, attività e note...",
      hint: "Scrivi almeno %{min} caratteri per cercare",
      empty: "Nessun risultato",
      error: "La ricerca non è disponibile in questo momento. Riprova.",
      untitled_result: "(senza titolo)",
      show_more: "Mostra altri",
      groups: {
        contacts: "Contatti",
        companies: "Organizzazioni",
        deals: "Trattative",
        tasks: "Attività",
        contact_notes: "Note sui contatti",
        deal_notes: "Note sulle trattative",
      },
    },
    image_editor: {
      change: "Cambia",
      drop_hint: "Trascina un file da caricare, oppure clicca per sceglierlo.",
      editable_content: "Contenuto modificabile",
      title: "Carica e ridimensiona l'immagine",
      update_image: "Aggiorna immagine",
    },
    import: {
      action: {
        download_error_report: "Scarica il rapporto degli errori",
        import: "Importa",
        import_another: "Importa un altro file",
      },
      error: {
        unable: "Impossibile importare questo file.",
      },
      idle: {
        description_1:
          "Puoi importare utenti, organizzazioni, contatti, note e attività.",
        description_2:
          "I dati devono essere in un file JSON come quello di esempio:",
      },
      status: {
        all_success: "Tutti gli elementi sono stati importati.",
        complete: "Importazione completata.",
        failed: "Non riusciti",
        imported: "Importati",
        in_progress: "Importazione in corso: non lasciare questa pagina.",
        some_failed: "Alcuni elementi non sono stati importati.",
        table_caption: "Stato dell'importazione",
      },
      title: "Importa da JSON",
    },
    settings: {
      about: "Informazioni",
      companies: {
        sectors: "Settori",
      },
      dark_mode_logo: "Logo per il tema scuro",
      deals: {
        categories: "Categorie",
        currency: "Valuta",
        pipeline_help: "Scegli quali fasi contano come trattative in corso.",
        pipeline_statuses: "Fasi in corso",
        stages: "Fasi",
      },
      light_mode_logo: "Logo per il tema chiaro",
      notes: {
        statuses: "Stati",
      },
      reset_defaults: "Ripristina i valori predefiniti",
      save_error: "Impossibile salvare la configurazione",
      saved: "Configurazione salvata",
      saving: "Salvataggio...",
      tasks: {
        types: "Tipi",
      },
      preferences: "Preferenze",
      title: "Impostazioni",
      app_title: "Nome dell'app",
      sections: {
        branding: "Aspetto",
      },
      validation: {
        duplicate: "%{display_name} duplicate: %{items}",
        in_use:
          "Impossibile rimuovere %{display_name} ancora usate dalle trattative: %{items}",
        validating: "Verifica in corso…",
        entities: {
          categories: "categorie",
          stages: "fasi",
        },
      },
    },
    theme: {
      dark: "Scuro",
      label: "Tema",
      light: "Chiaro",
      system: "Sistema",
    },
    language: "Lingua",
    navigation: {
      label: "Navigazione del CRM",
    },
    profile: {
      add_secondary_email: "Aggiungi un'email",
      email_taken: "%{email} è già usata da un altro utente",
      no_secondary_emails: "Nessuna",
      secondary_email_invalid: "%{email} non è un indirizzo email valido",
      secondary_email_is_primary: "%{email} è già il tuo indirizzo principale",
      secondary_email_taken: "%{email} è già usata da un altro utente",
      too_many_secondary_emails:
        "Non puoi aggiungere più di 10 indirizzi email secondari",
      secondary_emails_help:
        "Altri indirizzi da cui invii email. Lasciane uno vuoto per toglierlo.",
      inbound: {
        description:
          "Puoi inviare email all'indirizzo di ricezione del server, ad esempio aggiungendolo nel campo %{field}. Tenax CRM legge le email e aggiunge note ai contatti corrispondenti.",
        title: "Email in arrivo",
      },
      mcp: {
        title: "Server MCP",
        description:
          "Usa questo URL per collegare il tuo assistente AI ai dati del CRM tramite il Model Context Protocol (MCP).",
      },
      password: {
        change: "Cambia password",
      },
      password_reset_sent:
        "Ti abbiamo inviato un'email per reimpostare la password",
      record_not_found: "Elemento non trovato",
      title: "Profilo",
      updated: "Profilo aggiornato",
      update_error: "Si è verificato un errore. Riprova",
    },
    validation: {
      invalid_url: "Deve essere un URL valido",
      invalid_linkedin_url: "L'URL deve essere di linkedin.com",
    },
  },
} satisfies CrmMessages;
